import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { authFetch, dataFetch, setSession, clearSession, getSession } from '../api';

const AuthContext = createContext();

const SUPER_ADMIN_EMAIL = 'tunde825@gmail.com';

const ROLE_TIMEOUT_MS = 10000;
const MAX_ROLE_RETRIES = 3;
const RETRY_BACKOFF_MS = [500, 1500, 3000]; // exponential-ish backoff

// ─── Helpers ──────────────────────────────────────────────────────────

function withTimeout(promise, ms, signal) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      signal?.abort();
      reject(new Error(`Role resolution timed out after ${ms}ms`));
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

async function fetchWithRetry(
  fetchFn,
  { maxRetries = MAX_ROLE_RETRIES, label = 'fetch', signal } = {}
) {
  let lastError = null;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (signal?.aborted) throw new Error('Aborted');
    try {
      const result = await fetchFn();
      // dataFetch never throws; treat network / 5xx envelopes as retryable failures.
      if (result?.status === 'error' && (!result?.httpStatus || result.httpStatus >= 500)) {
        throw new Error(result?.error || 'Request failed');
      }
      return result;
    } catch (err) {
      lastError = err;
      if (attempt < maxRetries) {
        const delay = RETRY_BACKOFF_MS[attempt] || 3000;
        await new Promise(r => setTimeout(r, delay));
      }
    }
  }
  throw new Error(`[${label}] Failed after ${maxRetries + 1} attempts: ${lastError?.message}`);
}

// ─── Role Resolution ──────────────────────────────────────────────────

async function resolveRoleFromDB(userId, email, { signal } = {}) {
  // 1. Super-admin check
  if ((email || '').toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase()) {
    return { role: 'superadmin', source: 'hardcoded', error: null };
  }

  let lookupError = null;

  // 2. Admin check via site_admins table
  try {
    const adminRes = await fetchWithRetry(
      () => {
        if (signal?.aborted) throw new Error('Aborted');
        return dataFetch(
          `read/site_admins_1741165000?email=${encodeURIComponent((email || '').toLowerCase())}&is_active=1&limit=1`
        );
      },
      { label: 'admin-check', signal }
    );

    if (adminRes?.status === 'success' && (adminRes?.data ?? []).length > 0) {
      return { role: 'admin', source: 'site_admins', error: null };
    }
  } catch (err) {
    console.error('[Auth] Admin role resolution error:', err);
    lookupError = err;
    // Continue to next check
  }

  // 3. User profile check
  try {
    const profileRes = await fetchWithRetry(
      () => {
        if (signal?.aborted) throw new Error('Aborted');
        return dataFetch(
          `read/user_profiles_1741165000?owner_id=${encodeURIComponent(userId)}&limit=1`
        );
      },
      { label: 'profile-check', signal }
    );

    if (profileRes?.status === 'success' && (profileRes?.data ?? []).length > 0) {
      const profile = profileRes.data[0];
      const resolvedRole = profile?.role || 'creator';
      return { role: resolvedRole, source: 'user_profile', error: null };
    }
  } catch (err) {
    console.error('[Auth] Profile role resolution error:', err);
    lookupError = err;
    // Continue to default
  }

  // A failed lookup must not silently downgrade the user; surface it instead.
  if (lookupError) throw lookupError;

  // 4. Default
  return { role: 'creator', source: 'default', error: null };
}

async function createUserProfile(userId) {
  try {
    const res = await dataFetch('create/user_profiles_1741165000', {
      method: 'POST',
      body: JSON.stringify({ owner_id: userId, role: 'creator' }),
    });
    if (res?.status === 'success') {
      console.log('[Auth] Created user profile for', userId);
    } else {
      console.error('[Auth] Failed to create user profile:', res?.error);
    }
  } catch (err) {
    console.error('[Auth] createUserProfile error:', err);
  }
}

// ─── Impersonable Roles ───────────────────────────────────────────────

export const IMPERSONABLE_ROLES = [
  {
    id: 'visitor',
    label: 'Visitor',
    desc: 'Unauthenticated guest — browsing public memorials only.',
    icon: 'FiEye',
    color: 'bg-gray-100 text-gray-600 border-gray-300',
  },
  {
    id: 'contributor',
    label: 'Contributor',
    desc: 'Signed-in user who can leave tributes, light candles, and sign guestbooks.',
    icon: 'FiHeart',
    color: 'bg-pink-50 text-pink-600 border-pink-200',
  },
  {
    id: 'memorial_owner',
    label: 'Memorial Owner',
    desc: 'Full control over a specific memorial — editing, moderating, managing access.',
    icon: 'FiHome',
    color: 'bg-blue-50 text-blue-600 border-blue-200',
  },
  {
    id: 'merchant',
    label: 'Merchant',
    desc: 'Marketplace seller — managing products, payouts, and shop settings.',
    icon: 'FiShoppingBag',
    color: 'bg-green-50 text-green-600 border-green-200',
  },
  {
    id: 'digital_heir',
    label: 'Digital Heir',
    desc: 'Designated inheritor of a memorial — can claim and manage it posthumously.',
    icon: 'FiGitBranch',
    color: 'bg-purple-50 text-purple-600 border-purple-200',
  },
  {
    id: 'estate_executor',
    label: 'Estate Executor',
    desc: 'Legal representative managing estate documents, DocuVault, and asset distribution.',
    icon: 'FiBriefcase',
    color: 'bg-amber-50 text-amber-600 border-amber-200',
  },
];

// ─── Provider ─────────────────────────────────────────────────────────

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Granular role resolution state
  const [roleLoading, setRoleLoading] = useState(false);
  const [roleError, setRoleError] = useState(null);
  const [roleResolved, setRoleResolved] = useState(false);
  const [roleResolveAttempt, setRoleResolveAttempt] = useState(0);

  const [impersonatedRole, setImpersonatedRole] = useState(null);

  // Abort controller ref to prevent race conditions
  const roleAbortRef = useRef(null);

  // ── Core role resolution ──────────────────────────────────────────

  const performRoleResolution = useCallback(async (userId, email, { force = false } = {}) => {
    // Abort any in-flight request
    if (roleAbortRef.current) {
      roleAbortRef.current.abort();
    }
    const controller = new AbortController();
    roleAbortRef.current = controller;

    setRoleLoading(true);
    setRoleError(null);
    setRoleResolved(false);

    try {
      const result = await withTimeout(
        resolveRoleFromDB(userId, email, { signal: controller.signal }),
        ROLE_TIMEOUT_MS,
        controller.signal
      );

      if (controller.signal.aborted) {
        throw new Error('Role resolution was aborted');
      }

      setRoleResolved(true);
      setRoleResolveAttempt(prev => prev + 1);
      return { success: true, role: result.role, source: result.source };
    } catch (err) {
      const msg = err?.message || 'Unknown role resolution error';
      // A superseded (aborted) request must not overwrite the newer one's state.
      if (roleAbortRef.current === controller) setRoleError(msg);
      console.error('[Auth] Role resolution failed:', msg);
      return { success: false, error: msg };
    } finally {
      if (roleAbortRef.current === controller) {
        setRoleLoading(false);
        roleAbortRef.current = null;
      }
    }
  }, []);

  // ── Manual retry ────────────────────────────────────────────────────

  const retryRoleResolution = useCallback(async () => {
    if (!user?.id) return;
    setRoleResolveAttempt(0);
    const res = await performRoleResolution(user.id, user.email, { force: true });
    if (res.success) {
      const updated = { ...user, role: res.role };
      setSession(updated);
      setUser(updated);
    }
  }, [user, performRoleResolution]);

  // ── Init ────────────────────────────────────────────────────────────

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const savedUser = getSession();
      if (savedUser?.id) {
        const res = await performRoleResolution(savedUser.id, savedUser.email);
        if (res.success) {
          const updated = { ...savedUser, role: res.role };
          setSession(updated);
          setUser(updated);
        } else {
          // Keep existing user but mark role unresolvable
          setUser(savedUser);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    };
    init();

    return () => {
      if (roleAbortRef.current) {
        roleAbortRef.current.abort();
      }
    };
  }, [performRoleResolution]);

  // ── Login ───────────────────────────────────────────────────────────

  const login = async (email, password) => {
    const res = await authFetch('sign-in/email', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (res?.status === 'success' && res?.data?.token && res?.data?.user) {
      const userId = res.data.user.id;
      const userEmail = res.data.user.email;

      const roleRes = await performRoleResolution(userId, userEmail);
      const role = roleRes.success ? roleRes.role : 'creator';

      const fullUser = {
        ...res.data.user,
        role,
        ownedMemorials: res.data.user.ownedMemorials || [],
      };
      setSession(fullUser);
      setUser(fullUser);
      return fullUser;
    }

    const msg = res?.error || res?.data?.message || 'Login failed';
    throw new Error(msg);
  };

  // ── Signup ────────────────────────────────────────────────────────

  const signup = async (email, password, name) => {
    const res = await authFetch('sign-up/email', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    });

    if (res?.status === 'success' && res?.data?.token && res?.data?.user) {
      const isSuperAdmin = (res.data.user.email || '').toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();
      const role = isSuperAdmin ? 'superadmin' : 'creator';

      if (!isSuperAdmin) {
        await createUserProfile(res.data.user.id);
      }

      const fullUser = {
        ...res.data.user,
        role,
        ownedMemorials: [],
      };
      setSession(fullUser);
      setUser(fullUser);
      return fullUser;
    }

    const msg = res?.error || res?.data?.message || 'Signup failed';
    throw new Error(msg);
  };

  // ── Logout ────────────────────────────────────────────────────────

  const logout = () => {
    clearSession();
    setUser(null);
    setImpersonatedRole(null);
    setRoleError(null);
    setRoleResolved(false);
    setRoleResolveAttempt(0);
    window.location.hash = '/';
  };

  // ── Impersonation ─────────────────────────────────────────────────

  const startImpersonation = (roleId) => {
    const isSuper = user?.role === 'superadmin' || (user?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();
    if (!isSuper) return;
    setImpersonatedRole(roleId);
  };

  const stopImpersonation = () => setImpersonatedRole(null);

  // ── Derived ───────────────────────────────────────────────────────

  const isImpersonating = !!impersonatedRole;
  const effectiveRole = isImpersonating ? impersonatedRole : user?.role;

  const permissions = useMemo(() => {
    const isSuperRaw = user?.role === 'superadmin' || (user?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();
    const activeRole = isImpersonating ? impersonatedRole : user?.role;

    return {
      isTrueSuperAdmin: () => !!isSuperRaw,

      isSuperAdmin: () => !isImpersonating && !!isSuperRaw,

      isGlobalAdmin: () => !isImpersonating && (['admin', 'superadmin'].includes(user?.role) || !!isSuperRaw),

      isMemorialOwner: (memorialId) => {
        if (!isImpersonating && (['admin', 'superadmin'].includes(user?.role) || isSuperRaw)) return true;
        if (isImpersonating && activeRole === 'memorial_owner') return true;
        return user?.ownedMemorials?.includes(memorialId);
      },

      canAccessAdminHub: () => !isImpersonating && (['admin', 'superadmin'].includes(user?.role) || !!isSuperRaw),

      canManageAdmins: () => !isImpersonating && !!isSuperRaw,

      canModerateGlobal: () => !isImpersonating && ['admin', 'superadmin'].includes(user?.role),

      canManageMerchants: () => !isImpersonating && ['admin', 'superadmin'].includes(user?.role),

      canHostWebinar: () => ['admin', 'superadmin', 'creator', 'speaker'].includes(activeRole),

      isSpeaker: () => ['admin', 'superadmin', 'creator', 'speaker'].includes(activeRole),

      canManageOwnMemorial: (memorialId) =>
        user?.ownedMemorials?.includes(memorialId) || (!isImpersonating && ['admin', 'superadmin'].includes(user?.role)),

      isMerchant: () => activeRole === 'merchant',
      isDigitalHeir: () => activeRole === 'digital_heir',
      isEstateExecutor: () => activeRole === 'estate_executor',
      isContributor: () => activeRole === 'contributor',
      isVisitor: () => activeRole === 'visitor',
    };
  }, [user, isImpersonating, impersonatedRole]);

  // ── Value ─────────────────────────────────────────────────────────

  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    signup,
    logout,
    permissions,
    SUPER_ADMIN_EMAIL,
    // Role resolution state
    roleLoading,
    roleError,
    roleResolved,
    roleResolveAttempt,
    retryRoleResolution,
    // Impersonation
    impersonatedRole,
    isImpersonating,
    effectiveRole,
    startImpersonation,
    stopImpersonation,
  }), [
    user, loading, permissions, SUPER_ADMIN_EMAIL,
    roleLoading, roleError, roleResolved, roleResolveAttempt,
    retryRoleResolution,
    impersonatedRole, isImpersonating, effectiveRole,
    startImpersonation, stopImpersonation,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};